import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://regard-de-nature.fr";

const NaturopatheVillefranche = () => {
  useSEO({
    title: "Naturopathe à Villefranche-de-Lauragais (31290) – Laura Elie",
    description:
      "Naturopathe à Villefranche-de-Lauragais : accompagnement personnalisé pour fatigue, stress, sommeil, digestion. Consultations le jeudi matin à La Maison Céleste avec Laura Elie.",
    canonical: `${SITE}/naturopathe-villefranche-de-lauragais`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Regard de Nature – Laura Elie, Naturopathe (Villefranche-de-Lauragais)",
      image: `${SITE}/regard-de-nature-logo.png`,
      "@id": `${SITE}/naturopathe-villefranche-de-lauragais`,
      url: `${SITE}/naturopathe-villefranche-de-lauragais`,
      telephone: "+33637031866",
      address: {
        "@type": "PostalAddress",
        streetAddress: "La Maison Céleste",
        addressLocality: "Villefranche-de-Lauragais",
        postalCode: "31290",
        addressCountry: "FR",
      },
      areaServed: [
        "Villefranche-de-Lauragais",
        "Montesquieu-Lauragais",
        "Avignonet-Lauragais",
        "Renneville",
        "Gardouch",
        "Saint-Léon",
        "Nailloux",
        "Caraman",
        "Baziège",
        "Lauragais",
      ],
      priceRange: "€€",
      openingHours: "Th 09:00-13:00",
    },
  });

  return (
    <SeoPageLayout
      h1="Naturopathe à Villefranche-de-Lauragais – Retrouver énergie, sommeil et équilibre"
      intro="Vous tirez sur la corde depuis des mois, votre digestion vous joue des tours, vos nuits ne réparent plus rien et le stress s'invite partout ? Vous n'êtes pas seul·e — et vous êtes au bon endroit pour faire le point, posément. Consultations à Villefranche-de-Lauragais (31290), à La Maison Céleste, chaque jeudi matin."
    >
      <SeoSection title="Une naturopathe à Villefranche-de-Lauragais, à votre écoute">
        <p>
          Je suis Laura Elie, naturopathe et iridologue. Je <strong>reçois souvent des personnes</strong> qui arrivent
          au cabinet avec le même ressenti : une <strong>fatigue qui s'installe</strong> sans raison claire, un{" "}
          <strong>stress difficile à relâcher</strong>, des <strong>digestions inconfortables</strong> ou un sommeil
          qui ne récupère plus. Si vous vous reconnaissez, vous n'avez rien à justifier — juste à être accueilli·e.
        </p>
        <p>
          Je consulte à <strong>Villefranche-de-Lauragais</strong>, au sein de <strong>La Maison Céleste</strong>,
          chaque <strong>jeudi matin</strong>. Ce lieu, pensé pour la détente et le ressourcement, offre un cadre
          apaisant pour prendre le temps d'observer votre vitalité, vos rythmes, vos besoins. Dans ma pratique, je
          privilégie un accompagnement <strong>personnalisé, progressif et ancré dans votre quotidien</strong> — pas
          de protocole calqué, pas de solution miracle, mais un travail de fond mené à votre rythme.
        </p>
        <p>
          La naturopathie n'est pas un acte médical et ne se substitue pas à un suivi par votre médecin traitant.
          C'est un accompagnement bien-être qui s'appuie sur des leviers naturels — alimentation, hygiène de vie,
          gestion du stress, plantes, élixirs floraux — pour vous aider à retrouver équilibre et énergie au quotidien.
        </p>
      </SeoSection>

      {/* Bloc différenciation */}
      <SeoSection title="Ce qui guide ma pratique">
        <p>
          Trois principes simples orientent chacune de mes consultations à Villefranche-de-Lauragais :
        </p>
        <SeoList
          items={[
            "Une approche progressive : pas de bouleversements brutaux, mais des ajustements qui tiennent dans la durée.",
            "Une adaptation à la vie réelle : votre travail, votre famille, vos contraintes — tout est pris en compte.",
            "Un travail en profondeur : on ne se contente pas du symptôme, on cherche ensemble ce qui l'a installé.",
          ]}
        />
        <p>
          C'est cette manière d'avancer, pas à pas et sans dogme, qui fait la différence pour les personnes que
          j'accompagne sur le territoire du Lauragais.
        </p>
      </SeoSection>

      <SeoSection title="Ce que vous pouvez retrouver avec cet accompagnement">
        <p>
          L'objectif n'est pas de tout changer du jour au lendemain, mais de remettre en mouvement ce qui s'est figé.
          Au fil des séances, <strong>beaucoup de personnes que j'accompagne</strong> à Villefranche-de-Lauragais
          constatent :
        </p>
        <SeoList
          items={[
            "Un regain d'énergie au quotidien, avec moins de coups de barre dans la journée.",
            "Un sommeil plus stable, des endormissements plus simples et des réveils plus reposés.",
            "Une digestion apaisée et un meilleur confort intestinal après les repas.",
            "Une capacité accrue à gérer le stress et à relâcher les tensions accumulées.",
            "Un meilleur équilibre global : émotionnel, alimentaire, et dans les rythmes de vie.",
          ]}
        />
        <p>
          Ces évolutions s'installent dans la durée, à mesure que les ajustements proposés trouvent leur place dans
          votre vie réelle. Je ne promets pas de résultats — je m'engage sur la qualité de l'écoute et la cohérence
          des pistes proposées.
        </p>
      </SeoSection>

      <SeoSection title="Pour quelles problématiques consulter ?">
        <p>
          Voici les motifs de consultation que <strong>j'observe le plus souvent</strong> au cabinet de
          Villefranche-de-Lauragais. Pour chacun, je vous propose des pistes concrètes, adaptées à votre rythme et
          à vos contraintes de vie.
        </p>

        <SeoSubSection title="Stress et charge mentale">
          <p>
            Tensions accumulées, irritabilité, sensation de ne jamais s'arrêter, difficulté à lâcher prise le soir…
            En consultation, nous explorons ensemble vos déclencheurs, vos ressources et des outils simples
            (respiration, plantes adaptogènes, élixirs floraux, hygiène du système nerveux) pour relâcher la pression
            au fil des semaines.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">
              En savoir plus sur l'accompagnement du stress
            </Link>
          </p>
        </SeoSubSection>

        <SeoSubSection title="Fatigue persistante et baisse d'énergie">
          <p>
            Réveil difficile, coup de barre l'après-midi, sensation de tirer sur la corde malgré le sommeil… La
            fatigue chronique a souvent plusieurs racines : sommeil de mauvaise qualité, alimentation déséquilibrée,
            surcharge émotionnelle, carences en micronutriments. Nous identifions les leviers prioritaires pour
            recharger durablement votre vitalité.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-fatigue" className="text-primary underline hover:text-secondary">
              Découvrir l'accompagnement de la fatigue
            </Link>
          </p>
        </SeoSubSection>

        <SeoSubSection title="Troubles digestifs">
          <p>
            Ballonnements, transit irrégulier, lourdeurs après les repas, sensibilités alimentaires… La sphère
            digestive est centrale en naturopathie. Nous travaillons sur l'hygiène alimentaire, la mastication, le
            soutien du foie, le confort intestinal et, si pertinent, l'intégration de plantes ou d'hydrolats adaptés.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-digestion" className="text-primary underline hover:text-secondary">
              Voir l'accompagnement digestion et confort intestinal
            </Link>
          </p>
        </SeoSubSection>

        <SeoSubSection title="Sommeil difficile et réveils nocturnes">
          <p>
            Endormissement long, réveils à 3 h du matin, sommeil non récupérateur… Le sommeil reflète souvent un
            système nerveux en alerte ou une hygiène de vie à réajuster. Nous mettons en place une routine du soir
            réaliste, des soutiens naturels (plantes calmantes, magnésium, élixirs floraux) et nous travaillons les
            causes profondes plutôt que les symptômes.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-sommeil" className="text-primary underline hover:text-secondary">
              Explorer l'accompagnement du sommeil
            </Link>
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Vous vous reconnaissez dans ces situations ?">
        <p>
          <strong>Beaucoup arrivent avec</strong> des phrases qui se ressemblent :
        </p>
        <SeoList
          items={[
            "« Je suis fatigué·e sans raison apparente, et le repos seul ne suffit plus à récupérer. »",
            "« Mes digestions sont devenues inconfortables, je ne sais plus quoi manger sans gêne. »",
            "« J'ai du mal à gérer mon stress et je sens que mon corps commence à donner des signaux. »",
            "« Je dors mal depuis plusieurs semaines et la journée s'en ressent. »",
            "« Je traverse une période de transition (cycle, périnatalité, changement professionnel) et j'ai besoin d'être soutenu·e. »",
            "« Je veux prévenir, prendre soin de moi, comprendre ce dont mon corps a besoin avant que les choses ne s'installent. »",
          ]}
        />
        <p>
          Si l'une de ces phrases résonne avec vous, une consultation peut être un bon point de départ pour faire le
          point et poser des bases concrètes.
        </p>
      </SeoSection>

      {/* CTA milieu de page */}
      <section className="my-10 sm:my-12 p-6 sm:p-8 bg-muted/40 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Prendre rendez-vous à Villefranche-de-Lauragais et faire le point sur votre énergie
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5">
          Consultations le jeudi matin à La Maison Céleste. Un temps pour vous, dans un cadre apaisant.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Commencer un accompagnement adapté à mon rythme
          </Button>
        </Link>
      </section>

      <SeoSection title="Le déroulement d'une consultation">
        <SeoSubSection title="1. Premier rendez-vous : le bilan de vitalité (1 h 30)">
          <p>
            Cette première séance est un vrai temps d'échange. Nous abordons votre histoire, votre alimentation,
            votre sommeil, votre niveau d'énergie, votre stress, vos antécédents et vos objectifs. Si pertinent,
            j'utilise l'<Link to="/iridologie" className="text-primary underline hover:text-secondary">iridologie</Link>{" "}
            comme outil d'observation complémentaire pour mieux comprendre votre terrain.
          </p>
        </SeoSubSection>
        <SeoSubSection title="2. Programme personnalisé">
          <p>
            À l'issue du bilan, je vous remets un programme d'hygiène de vie individualisé : conseils alimentaires
            concrets, routine quotidienne réaliste, suggestions de plantes, hydrolats, élixirs ou compléments si
            besoin. Tout est pensé pour s'intégrer dans votre quotidien, pas pour le bouleverser.
          </p>
        </SeoSubSection>
        <SeoSubSection title="3. Suivi régulier">
          <p>
            Les rendez-vous de suivi (45 min à 1 h) permettent d'ajuster les recommandations dans le temps, en
            fonction de vos ressentis, de vos progrès et des éventuels obstacles rencontrés. C'est dans ce travail
            de fond que les changements s'installent durablement.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Mon ancrage dans le Lauragais">
        <p>
          J'accompagne des personnes de <strong>Villefranche-de-Lauragais</strong> et des communes alentour :{" "}
          <strong>Montesquieu-Lauragais</strong>, Avignonet-Lauragais, Renneville, Gardouch, Saint-Léon, Nailloux,
          Caraman, Baziège, ou encore Auterive. Le Lauragais est une terre que j'aime profondément, et c'est ici
          que je souhaite exercer un accompagnement de proximité, humain et accessible.
        </p>
        <p>
          La Maison Céleste se situe au cœur de Villefranche-de-Lauragais, facilement accessible depuis l'A61, la
          gare SNCF et les communes du Lauragais. Stationnement aisé à proximité. Pour celles et ceux qui préfèrent
          un cadre plus rural, je consulte également au cabinet de{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>
          , à quelques minutes seulement.
        </p>
      </SeoSection>

      <SeoSection title="Mon approche : douce, progressive, réaliste">
        <p>
          Ce que je vous propose, c'est un accompagnement <strong>sur mesure</strong>, sans régime strict ni
          protocole standardisé. Chaque suivi part de <strong>votre vie réelle</strong> : votre travail, votre
          famille, vos contraintes, vos goûts. Mon rôle est de vous aider à identifier ce qui vous nourrit, ce qui
          vous épuise, et à poser des ajustements progressifs qui tiennent dans la durée.
        </p>
        <p>
          La transformation passe par de petits pas répétés, pas par des bouleversements brutaux. C'est cette
          philosophie qui guide chacune de mes consultations à Villefranche-de-Lauragais. Pour aller plus loin sur
          ma démarche, vous pouvez consulter{" "}
          <Link to="/mon-approche" className="text-primary underline hover:text-secondary">
            ma page « Mon approche »
          </Link>
          .
        </p>
      </SeoSection>

      {/* Mini FAQ */}
      <SeoSection title="Questions fréquentes">
        <SeoSubSection title="Combien de séances faut-il prévoir ?">
          <p>
            Tout dépend de votre situation. En général, je propose un <strong>bilan initial</strong> (1 h 30) puis{" "}
            <strong>2 à 4 séances de suivi</strong> espacées de quelques semaines. Certaines personnes viennent
            ponctuellement pour une question précise, d'autres souhaitent un accompagnement plus long sur plusieurs
            mois. Nous en discutons ensemble dès la première rencontre.
          </p>
        </SeoSubSection>
        <SeoSubSection title="À qui s'adresse la naturopathie ?">
          <p>
            À toute personne qui souhaite prendre soin de sa santé au sens large : adultes, jeunes adultes, femmes
            en période de transition, parents fatigués, personnes qui veulent prévenir plutôt que subir. La
            naturopathie ne remplace jamais un suivi médical, mais elle accompagne très bien le quotidien quand on
            cherche à retrouver de l'équilibre.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Est-ce adapté à mon rythme de vie ?">
          <p>
            Oui — c'est même un point central de ma pratique. Je ne propose jamais de protocole impossible à tenir.
            Les recommandations sont calibrées pour s'intégrer dans votre quotidien, avec vos contraintes
            professionnelles et familiales. L'idée, c'est que ce que vous mettez en place soit durable, pas
            spectaculaire trois jours puis abandonné.
          </p>
        </SeoSubSection>
      </SeoSection>

      {/* CTA fin de page */}
      <section className="mt-10 sm:mt-12 p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Et si vous preniez ce temps pour vous ?
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5 max-w-2xl mx-auto">
          Prendre rendez-vous à Villefranche-de-Lauragais, c'est faire le premier pas pour retrouver de l'énergie,
          un sommeil plus stable et un meilleur équilibre au quotidien. Je vous accueille chaque jeudi matin à
          La Maison Céleste.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Prendre rendez-vous à Villefranche-de-Lauragais
          </Button>
        </Link>
      </section>
    </SeoPageLayout>
  );
};

export default NaturopatheVillefranche;
